import { useCallback, useEffect, useMemo, useState } from 'react';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { toast } from 'react-toastify';
import { db } from '../../../config/firebaseConfig';
import { fetchCompletionMatrix, isOverdue } from '../../../utils/assignmentDeadlines';
import useEducationReportText from './educationReportText';
import { ALL_STATUSES, EMPTY_FILTERS, STATUS_EXCEL } from './educationReportColumns';

const appliesTo = (groups, role) => {
  const list = Array.isArray(groups) ? groups : [groups].filter(Boolean);
  return list.includes('Hepsi') || (Boolean(role) && list.includes(role));
};

const fullName = (emp) => `${emp.eName || ''} ${emp.eSurname || ''}`.trim();
const projectTitles = (emp) => (Array.isArray(emp.projects) ? emp.projects.map((project) => project?.titleShort).filter(Boolean) : []);
const sortTr = (list) => list.sort((a, b) => a.localeCompare(b, 'tr'));

/**
 * Data of Eğitim Raporları and Eğitim Yönetimi, from the real records (the
 * old pages showed a made-up status per row): SOPs and video trainings
 * assigned by group (employee role or 'Hepsi'), completion from the read
 * confirmations and the training progress (fetchCompletionMatrix, as SMO
 * Lead Raporları), the filters and the Excel export.
 *
 * `mode: 'smo'` (Eğitim Yönetimi) keeps only the employees working on a
 * project in the centers whose SMO is `smoId` (Centers.SMO, as the old page).
 */
const useEducationReport = ({ mode = 'all', smoId = null, profileLoading = false } = {}) => {
  const { label } = useEducationReportText();
  const isSmo = mode === 'smo';
  const [isLoading, setIsLoading] = useState(true);
  const [assignments, setAssignments] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [centers, setCenters] = useState([]);
  const [matrix, setMatrix] = useState(null);
  const [scope, setScope] = useState('all'); // all | SOP | Eğitim
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState(ALL_STATUSES);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [onlyOverdue, setOnlyOverdue] = useState(false);
  const [selectedId, setSelectedId] = useState(null);

  // SMO görünümü profil yüklenene kadar bekler; tüm çalışanlar görünümü
  // kullanıcıya bağlı değildir (bir kez yüklenir)
  const waiting = isSmo && profileLoading;
  const scopeKey = isSmo ? smoId : null;

  useEffect(() => {
    if (waiting) return undefined;
    let cancelled = false;
    const load = async () => {
      setIsLoading(true);
      try {
        // SMO'su bu kullanıcı olan merkezler (eski Eğitim Yönetimi sorgusu)
        const centersPromise = isSmo && scopeKey
          ? getDocs(query(collection(db, 'Centers'), where('SMO', '==', scopeKey)))
          : Promise.resolve(null);
        const [sopsSnap, educationsSnap, completion, centersSnap] = await Promise.all([
          getDocs(collection(db, 'SOPs')),
          getDocs(collection(db, 'IndicationEducations')),
          fetchCompletionMatrix(),
          centersPromise,
        ]);
        if (cancelled) return;
        const sops = sopsSnap.docs.map((d) => ({ id: d.id, ...d.data() })).map((sop) => ({
          id: sop.id, type: 'SOP', name: sop.name || sop.title || '', version: sop.versionNumber, group: sop.group, dueDate: sop.dueDate || null,
        }));
        // Yalnızca video içeren eğitimler gerçek atama sayılır (Profil ile aynı kural)
        const educations = educationsSnap.docs.map((d) => ({ id: d.id, ...d.data() })).filter((edu) => edu.videoUrl).map((edu) => ({
          id: edu.id, type: 'Eğitim', name: edu.title || edu.name || '', version: edu.versionNumber, group: edu.group, dueDate: edu.dueDate || null,
        }));
        let scopedEmployees = completion.employees;
        if (isSmo) {
          const centerList = centersSnap ? centersSnap.docs.map((d) => ({ id: d.id, ...d.data() })) : [];
          const centerIds = new Set(centerList.map((center) => center.id));
          // Merkezlerinden birindeki bir projede çalışanlar (eski sayfanın kuralı)
          scopedEmployees = completion.employees.filter((emp) => emp.projects?.some((project) => centerIds.has(project.centerId)));
          setCenters(centerList);
        }
        setAssignments([...sops, ...educations]);
        setEmployees(scopedEmployees);
        setMatrix(completion);
      } catch (error) {
        console.error('Error fetching education report:', error);
        toast.error(label('toast.loadError'));
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
    // Açılışta (SMO görünümünde kullanıcı belli olunca)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [waiting, isSmo, scopeKey]);

  /** One row per scoped employee with at least one assignment in `scope`. */
  const buildRows = useCallback((rowScope) => {
    if (!matrix) return [];
    const inScope = assignments.filter((item) => rowScope === 'all' || item.type === rowScope);
    return employees
      .map((emp) => {
        const assigned = inScope.filter((item) => appliesTo(item.group, emp.role));
        if (assigned.length === 0) return null;
        const isDone = (item) => (item.type === 'SOP' ? matrix.completedBySop : matrix.completedByEducation).get(item.id)?.has(emp.id) || false;
        const items = assigned.map((item) => ({ ...item, done: isDone(item), overdue: !isDone(item) && isOverdue(item.dueDate) }));
        const done = items.filter((item) => item.done).length;
        const sops = items.filter((item) => item.type === 'SOP');
        const trainings = items.filter((item) => item.type === 'Eğitim');
        let status = 'notStarted';
        if (done === items.length) status = 'completed';
        else if (done > 0) status = 'inProgress';
        return {
          id: emp.id,
          name: fullName(emp),
          eName: emp.eName || '',
          eSurname: emp.eSurname || '',
          email: emp.eMail || '',
          role: emp.role || '',
          department: emp.departman || '',
          projects: projectTitles(emp),
          items,
          total: items.length,
          done,
          pending: items.length - done,
          overdue: items.filter((item) => item.overdue).length,
          percent: Math.round((done / items.length) * 100),
          sopTotal: sops.length,
          sopDone: sops.filter((item) => item.done).length,
          eduTotal: trainings.length,
          eduDone: trainings.filter((item) => item.done).length,
          status,
        };
      })
      .filter(Boolean)
      .sort((a, b) => a.name.localeCompare(b.name, 'tr'));
  }, [assignments, employees, matrix]);

  const rows = useMemo(() => buildRows(scope), [buildRows, scope]);

  const filteredRows = useMemo(() => {
    const term = searchTerm.trim().toLocaleLowerCase('tr-TR');
    const includes = (value, text) => String(value).toLocaleLowerCase('tr-TR').includes(text);
    return rows.filter((row) => {
      if (term) {
        // Eğitim Yönetimi'nde departman ve proje de aranır (eski sayfa gibi)
        const haystack = isSmo ? [row.eName, row.eSurname, row.role, row.department, ...row.projects] : [row.eName, row.eSurname, row.role];
        if (!haystack.some((value) => includes(value, term))) return false;
      }
      if (filters.name && !includes(row.name, filters.name.toLocaleLowerCase('tr-TR'))) return false;
      if (filters.role && row.role !== filters.role) return false;
      if (filters.department && row.department !== filters.department) return false;
      if (filters.project && !row.projects.includes(filters.project)) return false;
      if (statusFilter !== ALL_STATUSES && row.status !== statusFilter) return false;
      if (onlyOverdue && row.overdue === 0) return false;
      return true;
    });
  }, [rows, searchTerm, filters, statusFilter, onlyOverdue, isSmo]);

  const stats = useMemo(() => rows.reduce((acc, row) => ({
    assigned: acc.assigned + row.total,
    done: acc.done + row.done,
    pending: acc.pending + row.pending,
    overdue: acc.overdue + row.overdue,
    overduePeople: acc.overduePeople + (row.overdue > 0 ? 1 : 0),
    people: acc.people + 1,
    completedPeople: acc.completedPeople + (row.status === 'completed' ? 1 : 0),
  }), { assigned: 0, done: 0, pending: 0, overdue: 0, overduePeople: 0, people: 0, completedPeople: 0 }), [rows]);

  const options = useMemo(() => ({
    role: sortTr(Array.from(new Set(rows.map((row) => row.role).filter(Boolean)))),
    department: sortTr(Array.from(new Set(rows.map((row) => row.department).filter(Boolean)))),
    project: sortTr(Array.from(new Set(rows.flatMap((row) => row.projects)))),
  }), [rows]);

  const selectedRow = useMemo(() => rows.find((row) => row.id === selectedId) || null, [rows, selectedId]);

  const handleFilter = useCallback((key, value) => setFilters((current) => ({ ...current, [key]: value })), []);
  const clearFilters = useCallback(() => {
    setFilters(EMPTY_FILTERS);
    setSearchTerm('');
    setStatusFilter(ALL_STATUSES);
    setOnlyOverdue(false);
  }, []);

  // Excel: herkes, SOP ve eğitimler birlikte (başlıklar Türkçe)
  const downloadExcel = async () => {
    try {
      const ExcelJS = (await import('exceljs')).default;
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet(isSmo ? 'Eğitim Yönetimi' : 'Eğitim Raporları');
      worksheet.columns = [
        { header: 'Ad', key: 'eName', width: 15 },
        { header: 'Soyad', key: 'eSurname', width: 15 },
        ...(isSmo ? [{ header: 'Proje', key: 'projects', width: 30 }, { header: 'Departman', key: 'department', width: 20 }] : []),
        { header: 'Rol', key: 'role', width: 22 },
        { header: 'Atanan SOP\'lar', key: 'sops', width: 40 },
        { header: 'Atanan Eğitimler', key: 'trainings', width: 40 },
        { header: 'Tamamlanan', key: 'done', width: 13 },
        { header: 'Toplam', key: 'total', width: 10 },
        { header: 'İlerleme (%)', key: 'percent', width: 13 },
        { header: 'Gecikmiş', key: 'overdue', width: 11 },
        { header: 'Durum', key: 'status', width: 15 },
        { header: 'Bekleyenler', key: 'pendingItems', width: 50 },
      ];
      buildRows('all').forEach((row) => {
        worksheet.addRow({
          eName: row.eName || '-',
          eSurname: row.eSurname || '-',
          projects: row.projects.join(', ') || '-',
          department: row.department || '-',
          role: row.role || '-',
          sops: row.items.filter((item) => item.type === 'SOP').map((item) => item.name).join(', '),
          trainings: row.items.filter((item) => item.type === 'Eğitim').map((item) => item.name).join(', '),
          done: row.done,
          total: row.total,
          percent: row.percent,
          overdue: row.overdue,
          status: STATUS_EXCEL[row.status],
          pendingItems: row.items.filter((item) => !item.done).map((item) => item.name).join(', '),
        });
      });
      worksheet.getRow(1).eachCell((cell) => {
        cell.font = { bold: true, color: { argb: 'FFE5E7EB' } };
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF111827' } };
        cell.alignment = { vertical: 'middle', horizontal: 'center' };
      });
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'education_reports.xlsx';
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success(label('toast.excelDone'));
    } catch (error) {
      console.error('Error downloading Excel file:', error);
      toast.error(label('toast.excelError'));
    }
  };

  return {
    isLoading: isLoading || waiting,
    rows,
    filteredRows,
    stats,
    options,
    roles: options.role,
    centers,
    employeeCount: employees.length,
    scope,
    setScope,
    searchTerm,
    setSearchTerm,
    statusFilter,
    setStatusFilter,
    filters,
    handleFilter,
    clearFilters,
    onlyOverdue,
    setOnlyOverdue,
    selectedRow,
    setSelectedId,
    downloadExcel,
  };
};

export default useEducationReport;
